'use client';

import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ActivityBanner from '../components/ActivityBanner';
import FaqAccordion from '../components/FaqAccordion';
import GoodList from './components/GoodList';
import ProductNavigation from './components/ProductNavigation';
import YouMightLike from './components/YouMightLike';
import { usePinkLeopardTracking } from './hooks/useBlackFridayTracking';
import {
  BlackFridayClientProps,
  BlackFridayPageData,
  CommodityVersion,
  FaqData,
  NavigationItem,
} from './types';

export default function BlackFridayClientPage({
  serverData,
  deviceType,
  commodityVersionList = [],
  navigationData = [],
  bannerData,
  faqData,
}: BlackFridayClientProps) {
  const [pageData] = useState<BlackFridayPageData>(serverData);
  const [inAppH5, setInAppH5] = useState(false);
  const [allRecommendListLoaded, setAllRecommendListLoaded] = useState(false);
  const [isUserInfoReady, setIsUserInfoReady] = useState(false);

  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const setIsShowDtBanner = useStore((state) => state.setIsShowDtBanner);
  const isShowDtBanner = useStore((state) => state.isShowDtBanner);
  const isWap = deviceType !== EPlatform.WEB;

  // 导航栏选中状态
  const [selectedNavIndex, setSelectedNavIndex] = useState(0);

  // Banner曝光状态
  const [bannerExposed, setBannerExposed] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // 商品区域refs
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isScrolling = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);
  const scrollEndCheckRef = useRef<NodeJS.Timeout | null>(null);
  const pendingScrollIndexRef = useRef<number | null>(null);

  // 缓存Banner数据和aspectRatio
  const actualBannerData = useMemo(() => {
    // 在移动端优先使用 wap_banner，PC端使用 banner
    if (isWap) {
      return bannerData || pageData?.wap_banner || pageData?.banner;
    }
    return bannerData || pageData?.banner;
  }, [bannerData, pageData?.banner, pageData?.wap_banner, isWap]);

  const bannerAspectRatio = useMemo(() => {
    if (typeof actualBannerData === 'object') {
      return actualBannerData.aspectRatio;
    }
    return pageData?.aspectRatio;
  }, [actualBannerData, pageData?.aspectRatio]);

  // 使用服务端传递的数据，避免客户端重复请求
  const actualCommodityVersionList: CommodityVersion[] = useMemo(() => {
    return commodityVersionList.length > 0
      ? commodityVersionList
      : pageData?.commodity_version || [];
  }, [commodityVersionList, pageData?.commodity_version]);

  const actualNavigationData: NavigationItem[] = useMemo(() => {
    return navigationData.length > 0 ? navigationData : pageData?.navigate || [];
  }, [navigationData, pageData?.navigate]);

  const actualFaqData: FaqData | null = useMemo(() => {
    return faqData || pageData?.faq || null;
  }, [faqData, pageData?.faq]);

  // 初始化埋点追踪
  const {
    trackPageView,
    trackPageLeave,
    trackNavTabClick,
    trackViewMoreClick,
    trackProductListPitsClick,
    trackProductListPitsExposure,
  } = usePinkLeopardTracking();

  // 检测是否在APP内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInApp = userAgent.includes('nextmar-xms-app');
      setInAppH5(isInApp);
      setIsUserInfoReady(!isInApp); // 非App环境默认为准备完成
    }
  }, []);

  // 使用 ref 跟踪是否已经初始化，避免重复调用
  const hasInitialized = useRef(false);

  // App通信初始化
  useEffect(() => {
    if (inAppH5 && !hasInitialized.current) {
      // 处理App通信逻辑
      const handleAppCommunication = async () => {
        try {
          // 标记为已初始化
          hasInitialized.current = true;

          // 清空当前用户信息
          setToken('');
          setUserInfo({});

          // 注册token回调通信
          appCommunicationManager.registerCallback(
            EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
            async (_token) => {
              logger.log('App登录token:', _token);
              if (_token !== 'ClearToken') {
                setToken(_token);
                // 获取用户信息
                await refreshAccountInfo();
                setTimeout(() => {
                  setIsUserInfoReady(true);
                }, 1000);
              } else {
                setIsUserInfoReady(true);
              }
            },
            {},
            'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
          );
        } catch (e) {
          logger.error('App通信初始化失败:', e);
          // 初始化失败时重置标记，允许重试
          hasInitialized.current = false;
        }
      };

      handleAppCommunication();

      // APP内时监听appView和appLeave事件
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);

      // 添加超时机制，防止App通信失败导致页面一直加载
      const timeout = setTimeout(() => {
        if (!isUserInfoReady) {
          logger.warn('App通信超时，将以游客状态继续渲染页面');
          setIsUserInfoReady(true);
        }
      }, 5000); // 5秒超时

      return () => {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
        clearTimeout(timeout);
      };
    }
  }, [inAppH5]);

  // 组件卸载时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
      // 清理滚动相关的定时器
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }
    };
  }, [inAppH5]);

  // 页面加载完成后触发页面浏览埋点，组件卸载时触发离开埋点
  useEffect(() => {
    if (pageData) {
      // 只在WAP端：进入页面时隐藏底部导航，等推荐商品加载完成后再显示
      if (isWap) {
        setTimeout(() => {
          setIsShowFooterNav(false);
        }, 50);
      }
      trackPageView();
    }
    return () => {
      trackPageLeave();
    };
  }, [isWap]);

  // Banner曝光检测
  useEffect(() => {
    if (!bannerRef.current || bannerExposed || !actualBannerData) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !bannerExposed) {
            setBannerExposed(true);
          }
        });
      },
      { threshold: 0.5 }, // 当banner 50%可见时触发
    );

    observer.observe(bannerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [actualBannerData, bannerExposed]);

  // 检测滚动是否结束的函数
  const checkScrollEnd = useCallback(() => {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (Math.abs(currentScrollTop - lastScrollTop.current) < 1) {
      // 滚动已停止
      isScrolling.current = false;
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
        scrollEndCheckRef.current = null;
      }
    } else {
      // 滚动仍在进行，更新位置并继续检测
      lastScrollTop.current = currentScrollTop;
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }
      scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);
    }
  }, []);

  // 滚动检测函数 - 处理导航栏选中状态
  const handleScroll = useCallback(() => {
    // 如果正在滚动（点击导航项触发的滚动），则禁用自动选中功能
    if (isScrolling.current) {
      // 在滚动过程中继续检测滚动是否结束
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      lastScrollTop.current = currentScrollTop;

      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }
      scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);
      return;
    }

    const windowHeight = window.innerHeight;

    // 找到当前最接近视口中心的区域
    let currentIndex = 0;
    let minDistance = Infinity;

    sectionRefs.current.forEach((ref, index) => {
      if (ref) {
        const rect = ref.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const viewportCenter = windowHeight / 2;
        const distance = Math.abs(sectionCenter - viewportCenter);

        if (distance < minDistance) {
          minDistance = distance;
          currentIndex = index;
        }
      }
    });

    if (currentIndex !== selectedNavIndex) {
      setSelectedNavIndex(currentIndex);
    }
  }, [selectedNavIndex, checkScrollEnd, isScrolling]);

  // 执行实际的滚动逻辑
  const executeScroll = useCallback(
    (index: number) => {
      const targetRef = sectionRefs.current[index];
      const isApp = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);
      if (targetRef) {
        // 清除之前的滚动检测定时器
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        if (scrollEndCheckRef.current) {
          clearTimeout(scrollEndCheckRef.current);
        }

        // 设置滚动状态
        isScrolling.current = true;

        // 记录当前滚动位置
        lastScrollTop.current = window.pageYOffset || document.documentElement.scrollTop;

        // 计算偏移量，考虑全局头部高度和导航栏高度
        const headerHeight = isApp
          ? 0
          : parseInt(
              getComputedStyle(document.documentElement).getPropertyValue(
                '--global-header-height',
              ) || '100',
              10,
            );

        let offset: number;

        // offset = headerHeight + .product-navigation 元素的高度
        const productNavElement = document.querySelector('.product-navigation');
        const navHeight = productNavElement
          ? productNavElement.getBoundingClientRect().height
          : 120; // 默认120px
        offset = headerHeight + navHeight;
        // 在 app 中需要额外向上滚动 11.733vw
        if (isWap && !isApp) {
          // 将 35px 转换为自适应值（基于 375px 基准宽度：56/375*100 = 14.933vw，然后转换为像素）
          const adaptiveOffset = (50 / 375) * window.innerWidth;
          offset -= adaptiveOffset;
        }

        const targetTop = targetRef.offsetTop - offset;

        window.scrollTo({
          top: targetTop,
          behavior: 'smooth',
        });

        // 开始检测滚动结束
        scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);

        // 备用的最大时间限制，防止检测失败
        scrollTimeoutRef.current = setTimeout(() => {
          isScrolling.current = false;
          if (scrollEndCheckRef.current) {
            clearTimeout(scrollEndCheckRef.current);
            scrollEndCheckRef.current = null;
          }
        }, 2000); // 最多2秒后强制重置
      }
    },
    [checkScrollEnd, deviceType, isWap],
  );

  // 平滑滚动到指定区域
  const scrollToSection = useCallback(
    (index: number) => {
      if (isShowDtBanner) {
        // 如果 banner 正在显示，先隐藏它并保存待执行的滚动索引
        setIsShowDtBanner(false);
        pendingScrollIndexRef.current = index;
      } else {
        // 如果 banner 未显示，直接执行滚动
        executeScroll(index);
      }
    },
    [isShowDtBanner, setIsShowDtBanner, executeScroll],
  );

  // 监听 isShowDtBanner 变化，当变为 false 时执行待处理的滚动
  useEffect(() => {
    if (!isShowDtBanner && pendingScrollIndexRef.current !== null) {
      const index = pendingScrollIndexRef.current;
      pendingScrollIndexRef.current = null;
      // 等待 300ms 确保 banner 关闭动画完成后再执行滚动
      setTimeout(() => {
        executeScroll(index);
      }, 300);
    }
  }, [isShowDtBanner, executeScroll]);

  // 添加滚动监听
  useEffect(() => {
    let ticking = false;

    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [handleScroll]);

  // 转换商品数据格式
  const transformProductData = (product: any) => {
    return {
      id: product.id,
      name: product.name,
      short_name: product.short_name,
      shop_sku: product.shop_sku,
      business_sku: product.business_sku,
      image: product.image,
      price_info: product.price_info,
      is_sold_out: product.stock === 0,
      ...product, // 保留其他所有属性
    };
  };

  // YouMightLike商品点击处理
  const handleYouMightLikeProductClick = useCallback(
    (product: any, index: number) => {
      trackProductListPitsClick(product, index, '瀑布流版位');
    },
    [trackProductListPitsClick],
  );

  // YouMightLike商品曝光处理
  const handleYouMightLikeProductExp = useCallback(
    (product: any, index: number) => {
      trackProductListPitsExposure(product, index, '瀑布流版位');
    },
    [trackProductListPitsExposure],
  );

  // 推荐商品加载完成回调
  const handleRecommendLoadComplete = useCallback(
    (isComplete: boolean) => {
      setAllRecommendListLoaded(isComplete);
      // 只在WAP端：推荐商品加载完成后才显示底部导航
      if (isComplete && isWap) {
        setIsShowFooterNav(true);
      }
    },
    [isWap, setIsShowFooterNav],
  );

  if (!pageData) {
    return null;
  }

  return (
    <Box className="w-full bg-[#1A0707]">
      {/* Banner区域 */}
      {actualBannerData && typeof actualBannerData === 'object' && (
        <div ref={bannerRef}>
          <ActivityBanner
            data={{
              alt: actualBannerData.image_alt,
              image: actualBannerData.image,
              type: actualBannerData.type,
              jump_content: actualBannerData.jump_content as any,
            }}
            aspectRatio={bannerAspectRatio}
          />
        </div>
      )}

      {/* 商品区域 */}
      <div>
        {/* 商品区域导航栏 */}
        {actualNavigationData && actualNavigationData.length > 0 && (
          <ProductNavigation
            navigationData={actualNavigationData}
            selectedIndex={selectedNavIndex}
            isSticky
            disableAutoSelect={isScrolling.current}
            onNavigationClick={(item, index) => {
              logger.log(`导航项被点击: ${item.title}, 索引: ${index}`);
              // 触发导航tab点击埋点
              trackNavTabClick(item.title, index);
              setSelectedNavIndex(index);
              scrollToSection(index);
            }}
          />
        )}

        {/* 循环渲染 commodity_version 数组中的每个商品组 */}
        {actualCommodityVersionList
          ?.filter((group) => group.product_list && group.product_list.length > 0)
          .map((commodityGroup, validIndex) => {
            // 使用默认的促销图片，如果banner为空
            const defaultPromoImage =
              'https://s3.zeelool.com/admin/shop/home/default-promo-banner.png';

            // 获取商品组的促销图片：优先使用商品组的image字段，其次使用banner，最后使用默认图片
            const getPromoImage = () => {
              // 使用商品组最外层的image字段
              if (commodityGroup.image) {
                return commodityGroup.image;
              }
              return typeof actualBannerData === 'string' ? actualBannerData : defaultPromoImage;
            };

            return (
              <div
                key={`commodity-group-${commodityGroup.product_group_id}-${validIndex}`}
                ref={(el) => {
                  sectionRefs.current[validIndex] = el;
                }}
              >
                <GoodList
                  index={validIndex}
                  tabName={actualNavigationData?.[selectedNavIndex]?.title || 'unknown'}
                  promoImage={{
                    image: getPromoImage(),
                    wap_image: commodityGroup.wap_image,
                    title: `商品组 ${commodityGroup.product_group_id}`,
                    word: `商品组 ${commodityGroup.product_group_id}`,
                    image_alt: commodityGroup.image_alt || '',
                    description: `商品组 ${commodityGroup.product_group_id} 描述`,
                    aspectRatio: '1920/580',
                    jump_type: 1,
                    jump_value: commodityGroup.product_group_id.toString(),
                  }}
                  products={commodityGroup.product_list.map(transformProductData)}
                  deviceType={deviceType}
                  onProductClick={(product, productIndex) => {
                    // 触发商品列表坑位点击埋点
                    trackProductListPitsClick(
                      product,
                      productIndex,
                      '商品版位',
                      actualNavigationData?.[validIndex]?.title,
                    );
                  }}
                  onProductExp={(product, productIndex) => {
                    // 触发商品列表坑位曝光埋点
                    trackProductListPitsExposure(
                      product,
                      productIndex,
                      '商品版位',
                      actualNavigationData?.[validIndex]?.title,
                    );
                  }}
                  viewMoreConfig={{
                    groupId: commodityGroup.product_group_id.toString(),
                    text: 'VIEW MORE',
                    showButton: true,
                  }}
                  onViewMoreClick={() => {
                    trackViewMoreClick(actualNavigationData?.[validIndex]?.title);
                  }}
                />
              </div>
            );
          })}

        {/* 如果没有 commodity_version 数据，显示占位信息 */}
        {(!actualCommodityVersionList || actualCommodityVersionList.length === 0) && (
          <div className="w-full h-64 flex items-center justify-center bg-gray-100">
            <p className="text-gray-500">暂无商品数据</p>
          </div>
        )}
      </div>

      {/* YouMightLike猜你喜欢推荐商品 */}
      <YouMightLike
        deviceType={deviceType}
        onProductClick={handleYouMightLikeProductClick}
        onProductExp={handleYouMightLikeProductExp}
        onLoadComplete={handleRecommendLoadComplete}
        isInApp={inAppH5}
      />

      {/* FAQ区域 - 只在推荐商品加载完成后显示 */}
      {allRecommendListLoaded && actualFaqData && (
        <div className="faq-section">
          <FaqAccordion
            backgroundColor="bg-[#F4F4F4]"
            faqData={{ ...actualFaqData, title: actualFaqData.title }}
          />
        </div>
      )}
    </Box>
  );
}
